import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import api from '../../lib/api';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface VideoAvatar {
  id: string;
  name: string;
  previewImageUrl: string | null;
  gender: string | null;
  vendorType: string;
  vendorReplicaId: string;
  orgId: string | null;
  organization: { id: string; name: string } | null;
}

interface VideoAvatarForm {
  name: string;
  previewImageUrl: string;
  gender: string;
  vendorType: string;
  vendorReplicaId: string;
  orgId: string;
}

const emptyForm = (): VideoAvatarForm => ({
  name: '',
  previewImageUrl: '',
  gender: '',
  vendorType: 'tavus',
  vendorReplicaId: '',
  orgId: '',
});

export function AdminVideoAvatarsPage() {
  const [avatars, setAvatars] = useState<VideoAvatar[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<VideoAvatarForm>(emptyForm());
  const [avatarIdToDelete, setAvatarIdToDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAvatars();
  }, []);

  const loadAvatars = () => {
    api
      .get<VideoAvatar[]>('/admin/video-avatars')
      .then((res) => setAvatars(res.data))
      .catch(() => {});
  };

  const handleEdit = (a: VideoAvatar) => {
    setForm({
      name: a.name,
      previewImageUrl: a.previewImageUrl ?? '',
      gender: a.gender ?? '',
      vendorType: a.vendorType,
      vendorReplicaId: a.vendorReplicaId,
      orgId: a.orgId ?? '',
    });
    setEditingId(a.id);
    setShowForm(true);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        name: form.name,
        previewImageUrl: form.previewImageUrl.trim() || null,
        gender: form.gender || null,
        vendorType: form.vendorType,
        vendorReplicaId: form.vendorReplicaId,
        orgId: form.orgId.trim() || null,
      };
      if (editingId) {
        await api.patch(`/admin/video-avatars/${editingId}`, payload);
      } else {
        await api.post('/admin/video-avatars', payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      loadAvatars();
    } catch (err) {
      console.error('Failed to save video avatar', err);
      Sentry.captureException(err);
      setError('Failed to save. Check the fields and try again.');
    }
  };

  const confirmDelete = async () => {
    if (!avatarIdToDelete) return;
    const id = avatarIdToDelete;
    setAvatarIdToDelete(null);
    try {
      await api.delete(`/admin/video-avatars/${id}`);
      loadAvatars();
    } catch (err) {
      console.error('Failed to delete video avatar', err);
      Sentry.captureException(err);
    }
  };

  const globalAvatars = avatars.filter((a) => a.orgId === null);
  const tenantAvatars = avatars.filter((a) => a.orgId !== null);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Video Avatars</h1>
          <p className="text-gray-600 mt-1">
            Manage video avatars for verbal practice sessions. Vendor details are never shown to
            tenant users — they only see avatar names and preview images.
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingId(null);
            setForm(emptyForm());
            setError(null);
          }}
          className="bg-red-700 text-white px-4 py-2 rounded-md font-medium hover:bg-red-800"
        >
          New Video Avatar
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg border mb-8">
          <h2 className="text-lg font-semibold mb-4">
            {editingId ? 'Edit' : 'Create'} Video Avatar
          </h2>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 max-w-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  placeholder="e.g. John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender (optional)
                </label>
                <select
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">Not specified</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="nonbinary">Non-binary</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preview image URL (optional)
              </label>
              <input
                type="url"
                value={form.previewImageUrl}
                onChange={(e) => setForm({ ...form, previewImageUrl: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="https://..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Publicly accessible image shown to tenant admins in the avatar picker.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor type</label>
                <select
                  value={form.vendorType}
                  onChange={(e) => setForm({ ...form, vendorType: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="tavus">Tavus</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor replica ID
                </label>
                <input
                  type="text"
                  required
                  value={form.vendorReplicaId}
                  onChange={(e) => setForm({ ...form, vendorReplicaId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm font-mono"
                  placeholder="e.g. r72f7f7f7c8b"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenant (org ID) — leave blank for global catalog
              </label>
              <input
                type="text"
                value={form.orgId}
                onChange={(e) => setForm({ ...form, orgId: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm font-mono"
                placeholder="Leave blank for global catalog"
                disabled={!!editingId}
              />
              <p className="text-xs text-gray-500 mt-1">
                Global catalog avatars can be enabled per tenant below. Tenant-exclusive avatars are
                only visible to that tenant.
              </p>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-red-700 text-white px-6 py-2 rounded-md font-medium hover:bg-red-800"
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="border text-gray-700 px-6 py-2 rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {globalAvatars.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Global catalog</h2>
          <p className="text-sm text-gray-600 mb-4">
            These avatars can be enabled for individual tenants via the tenant detail page.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {globalAvatars.map((a) => (
              <AvatarCard
                key={a.id}
                avatar={a}
                onEdit={() => handleEdit(a)}
                onDelete={() => setAvatarIdToDelete(a.id)}
              />
            ))}
          </div>
        </div>
      )}

      {tenantAvatars.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Tenant-exclusive</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tenantAvatars.map((a) => (
              <AvatarCard
                key={a.id}
                avatar={a}
                onEdit={() => handleEdit(a)}
                onDelete={() => setAvatarIdToDelete(a.id)}
              />
            ))}
          </div>
        </div>
      )}

      {avatars.length === 0 && !showForm && (
        <p className="text-gray-500">No video avatars yet. Create one to get started.</p>
      )}

      <ConfirmDialog
        open={avatarIdToDelete !== null}
        title="Delete video avatar?"
        description="Delete this video avatar? Any personas using it will lose their video avatar assignment."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setAvatarIdToDelete(null)}
      />
    </div>
  );
}

function AvatarCard({
  avatar,
  onEdit,
  onDelete,
}: {
  avatar: VideoAvatar;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="bg-white p-5 rounded-lg border">
      <div className="flex items-start gap-3 mb-3">
        {avatar.previewImageUrl ? (
          <img
            src={avatar.previewImageUrl}
            alt={avatar.name}
            className="w-12 h-12 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0 text-gray-400 text-lg font-semibold select-none">
            {avatar.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900">{avatar.name}</h3>
          {avatar.gender && <p className="text-xs text-gray-500 capitalize">{avatar.gender}</p>}
          {avatar.organization && (
            <p className="text-xs text-blue-600 truncate">Exclusive: {avatar.organization.name}</p>
          )}
        </div>
      </div>
      <div className="text-xs text-gray-400 space-y-0.5 mb-3">
        <p>
          <span className="font-medium text-gray-600">Vendor:</span> {avatar.vendorType}
        </p>
        <p className="font-mono break-all">{avatar.vendorReplicaId}</p>
      </div>
      <div className="flex gap-2">
        <button onClick={onEdit} className="text-sm text-blue-600 hover:underline">
          Edit
        </button>
        <button onClick={onDelete} className="text-sm text-red-600 hover:underline">
          Delete
        </button>
      </div>
    </div>
  );
}
